/** The outside content a turn read, which sends its writes to approval (WEB-12516). */
export const UNTRUSTED_SOURCES = ['page', 'knowledge', 'web', 'email', 'transcript', 'integration', 'file'] as const;

export type UntrustedSource = (typeof UNTRUSTED_SOURCES)[number];

export const asUntrustedSource = (value: unknown): UntrustedSource | undefined =>
	UNTRUSTED_SOURCES.find(source => source === value);

export type MultiActionProperty = {
	name: string;
	value: unknown;
};

export type MultiActionFilter = {
	field: string;
	comparison: string;
	value: unknown;
};

export type MultiActionStatus = 'pending' | 'cancelled' | 'executing' | 'completed' | 'failed';

/** A proposed contact-discovery job: read these companies' websites and add the
 *  people found as contacts. Approving queues the same background multi-action
 *  the company list's "Find contacts from company website" button queues, so
 *  nothing is written until the user confirms (WEB-11112). */
export type ContactScrapeConfig = {
	id: string;
	clientIds: number[];
	companyCount: number;
	/** First few company names, for display. Not the full set. */
	sampleNames: string[];
	/** Companies left out for having no website on record. */
	skippedNoWebsite: number;
	/** What the job will cost, resolved server-side from the same unit the scraper bills. */
	cost?: { kind: 'credits'; credits: number } | { kind: 'addon' } | { kind: 'unknown' };
	/** The background job's notification, so the card can follow it to completion. */
	notificationId?: number;
	/** Terminal state of the job itself, distinct from `status` (which is the approval's). */
	jobOutcome?: 'done' | 'failed';
	/** Contacts created, as reported by the finished job. */
	contactsAdded?: number;
	status: MultiActionStatus;
	resultMessage?: string;
	approvalId?: number | null;
};

export type ProspectingAddAction =
	| 'prospecting_add_company'
	| 'prospecting_add_contacts'
	| 'prospecting_add_companies_bulk';

/** A drafted prospecting add (WEB-11807). Approving runs the prospecting import. */
export type ProspectingAddConfig = {
	id: string;
	action: ProspectingAddAction;
	companyName?: string;
	orgNumber?: string;
	contacts: { name: string; role?: string }[];
	/** Bulk add only (WEB-12974): how many companies, the campaign, the credits, a few names. */
	companyCount?: number;
	campaignName?: string;
	credits?: number;
	sample?: string[];
	accountManagerName?: string;
	country?: string;
	status: MultiActionStatus;
	resultMessage?: string;
	/** The company the import created or updated, once approved. */
	clientId?: number;
	/** Approved, but the import matched a company already in the CRM instead of adding one (WEB-14401). */
	linkedExisting?: boolean;
	approvalId?: number | null;
};

export type MultiActionConfig = {
	/** Approval created date (YYYY-MM-DD). Set by the company-card section so
	 *  cards without a natural date still show one; unset in the AI Workspace. */
	createdDate?: string;
	id: string;
	task: 'Update' | 'Create' | 'Delete';
	entity: string;
	action: string;
	entityRoute: string;
	filters: MultiActionFilter[];
	properties: MultiActionProperty[];
	selectedIds: number[];
	estimatedCount: number;
	description: string;
	status: MultiActionStatus;
	resultMessage?: string;
	/** Set when the proposal is backed by an agent_approval_request row.
	 *  WEB-6457: chat proposals persist across reload via this id. */
	approvalId?: number | null;
	resultEntityType?: string | null;
	resultEntityId?: number | null;
};

export type WriteProposal = {
	/** Approval created date (YYYY-MM-DD); see MultiActionConfig.createdDate. */
	createdDate?: string;
	entity: string;
	action: 'create' | 'update' | 'delete';
	data: Record<string, unknown>;
	status: 'pending' | 'saving' | 'saved' | 'cancelled' | 'error';
	/** Estimated audience size for segment proposals, computed at draft time
	 *  so the preview card can show it before the user confirms (WEB-7261). */
	matchingContacts?: number;
	/** Human-readable name of the target record, resolved at draft time so the
	 *  card can show e.g. "Uppdatera Företag · Acme Corp" instead of just the id.
	 *  Only set for update/delete when the payload itself lacks a title-ish key. */
	entityDisplayName?: string;
	result?: Record<string, unknown>;
	error?: string;
	approvalId?: number | null;
	resultEntityType?: string | null;
	resultEntityId?: number | null;
};

export type WriteProposalGroup = {
	items: WriteProposal[];
};

export type EmailDraft = {
	/** Approval created date (YYYY-MM-DD); see MultiActionConfig.createdDate. */
	createdDate?: string;
	contactIds: number[];
	subject: string;
	body: string;
	ccContactIds?: number[];
	clientId?: number;
	/** Files attached to the email, resolved to id + filename for display.
	 *  Agent-chosen and not user-editable. */
	attachments?: { id: number; name: string }[];
	status: 'pending' | 'sending' | 'sent' | 'cancelled' | 'error';
	error?: string;
	/** Set when the proposal is backed by an agent_approval_request row.
	 *  WEB-6572: chat email drafts persist across reload via this id. */
	approvalId?: number | null;
	/** Mail ids returned by the send executor; populated when the proposal
	 *  resolves to status='sent'. Used to surface an "Open" link to the
	 *  sent mail drawer. */
	mailIds?: number[];
};

export type OfferDraftLineItem = {
	productId: number;
	/** Catalogue name, display-only, so the rep can verify the agent's match. */
	productName?: string;
	quantity: number;
	unitPrice?: number;
	discount?: number;
};

export type OfferDraft = {
	/** Approval created date (YYYY-MM-DD); see MultiActionConfig.createdDate. */
	createdDate?: string;
	clientId: number;
	contactId: number;
	products: OfferDraftLineItem[];
	currency: string;
	validUntil: string;
	description?: string;
	notes?: string;
	status: 'pending' | 'saving' | 'saved' | 'cancelled' | 'error';
	error?: string;
	/** Set when the draft is backed by an agent_approval_request row (persists across reload). */
	approvalId?: number | null;
	/** Opportunity (order) created when the offer is approved. */
	resultEntityType?: string | null;
	resultEntityId?: number | null;
};

/** One line on a tool proposal: `key` is translated, `value` is shown as it is. */
export type ToolProposalField = { key: string; value: string };

/**
 * A chat tool call the agent's permissions don't cover (WEB-12517) — an email or
 * Slack message to a colleague, an integration write, an automation or agent
 * change, a hand-over. Nothing is editable: it runs exactly as shown on approve.
 */
export type ToolProposal = {
	tool: string;
	/** The permission that would let it run without asking, if the tool has one. */
	capability: string | null;
	fields: ToolProposalField[];
	status: 'pending' | 'saving' | 'saved' | 'cancelled' | 'error';
	error?: string;
	approvalId: number;
};

export type MessageAttachment = {
	name: string;
	mimeType: string;
	sizeBytes?: number;
	url?: string;
};

export type ChartRow = { key: string; value: number };

export const CHART_TYPES = ['bar', 'line', 'area', 'donut', 'bigNumber', 'table'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

export type ChartSpec = {
	chartType: ChartType;
	title: string;
	rows: ChartRow[];
	xLabel?: string;
	yLabel?: string;
	currency?: string;
};

// A single item within an approvals review group. Today the agent can produce
// CRM-write approvals AND send-email approvals in the same turn (post-meeting
// agent does both), so the group renders either kind in a unified list with
// one header + one bulk-action footer.
export type ApprovalGroupItem = { kind: 'write'; proposal: WriteProposal } | { kind: 'email'; draft: EmailDraft };

export type ApprovalsGroup = { items: ApprovalGroupItem[] };

/**
 * One filter the assistant set on a prospecting session, as the panel event and
 * the message metadata carry it. `labelKey` travels with the filter because the
 * registry that owns it lives in base/ and the browser cannot read it.
 */
export type ProspectingPanelFilter = {
	key: string;
	value: unknown;
	setBy?: string;
	reasoning?: string;
	labelKey?: string;
};

export type ProspectingFilterPanel = {
	conversationId: number;
	country: string;
	entityMode: string;
	filters: ProspectingPanelFilter[];
	unresolved?: string[];
};

/**
 * What a prospecting ask could not reach, from `proposeCoverageEscalation`.
 * Nothing runs off this - it is the explanation the rep decides against.
 */
export type ProspectingCoveragePanel = {
	outOfCoverage: { ask: string; reason: string }[];
	instantAlternative?: string;
	covered?: { countries: string[]; filterCount: number; unavailableCount: number };
};

export type AssistantMessage = {
	id?: number;
	role: 'user' | 'assistant';
	content: string;
	regDate?: string;
	images?: string[];
	attachments?: MessageAttachment[];
	metadata?: Record<string, unknown>;
	multiAction?: MultiActionConfig;
	contactScrape?: ContactScrapeConfig;
	writeProposal?: WriteProposal;
	// Mixed approvals review group. Held under the legacy `writeProposalGroup`
	// name to keep persisted-message and live-stream code paths talking the
	// same shape; the renamed item type makes the union explicit at use sites.
	writeProposalGroup?: ApprovalsGroup;
	emailDraft?: EmailDraft;
	offerDraft?: OfferDraft;
	toolProposal?: ToolProposal;
	prospectingAdd?: ProspectingAddConfig;
	chart?: ChartSpec;
	prospectingFilters?: ProspectingFilterPanel;
	prospectingCoverage?: ProspectingCoveragePanel;
	thinking?: string;
	suggestedReplies?: string[];
	/** Outside content the turn read before proposing its cards (WEB-12516). */
	untrustedSource?: UntrustedSource;
	/**
	 * Surface-specific activity list for this reply — the Pages build ledger
	 * (WEB-10046). Opaque here on purpose: MessageThread only hands it back to the
	 * caller's `renderActivities`, so the shared type doesn't take on a Pages
	 * concept and every other surface leaves it undefined.
	 */
	activities?: unknown;
	/**
	 * The element the user pointed at in the page preview before sending this
	 * message (WEB-10047). Rendered as a chip above their bubble, like an
	 * attachment. Pages only — every other surface leaves it undefined.
	 */
	picked?: { kind: string; text: string };
	/**
	 * 1-based index of this reply among the turns that changed the page's code
	 * (WEB-10047). Absent on a turn that only talked, and on every other surface.
	 *
	 * Deliberately NOT called a version: authoring through chat writes the working
	 * draft and never archives a `previousVersions` entry — those are created only
	 * on an explicit Save (see agentPage.ts `_beforeSave`). Labelling these
	 * "Version n" put a chip reading v13 next to a header reading v2 and implied a
	 * rollback target that does not exist.
	 */
	revision?: number;
};

export type Conversation = {
	id: number;
	title: string;
	contextType: string;
	contextId?: number;
	/** Set when this is a delegated child conversation — the delegating conversation. */
	parentConversationId?: number | null;
	regDate: string;
	modDate: string;
	starred?: boolean;
};
